import startCase from "lodash.startcase";
import { X } from "lucide-react";
import React, { useState } from "react";
import { useImmer } from "use-immer";

import {
  IgAutocomplete,
  IgButton,
  IgCheckbox,
  IgLink,
  IgPaper,
  IgSwitch,
  IgTextField,
  IgTypography,
  IgTypographyTooltip,
} from "@intelligems/ig-theme";
import {
  ExperienceWebhookActionType,
  ExperienceWebhookActionTypeList,
  WebhookDto,
} from "@intelligems/ig-types";
import { Grid, MenuItem } from "@mui/material";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { useDeleteWebhook } from "@src/api/webhooks/delete-webhook";
import { useGetWebhooks } from "@src/api/webhooks/get-webhooks";
import { usePatchWebhook } from "@src/api/webhooks/patch-webhook";
import { usePostWebhook } from "@src/api/webhooks/post-webhook";
import { urlRegex } from "@src/utils/constants";
import { zapierWebhookExample } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

import { WebhookTable } from "./WebhookTable";

interface WebhookFormData {
  url: string;
  actionTypes: ExperienceWebhookActionType[];
  enabled: boolean;
}

export const WebhookSettings = () => {
  const webhooks = useGetWebhooks();
  const createWebhook = usePostWebhook();
  const updateWebhook = usePatchWebhook();
  const deleteWebhook = useDeleteWebhook();

  const [formData, setFormData] = useImmer<WebhookFormData>({
    url: "",
    actionTypes: ["startExperience"],
    enabled: true,
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<boolean>(false);

  const isLoading =
    webhooks.isLoading ||
    createWebhook.isPending ||
    updateWebhook.isPending ||
    deleteWebhook.isPending;

  const handleUrlChange = (value: string) => {
    setFormData((draft) => {
      draft.url = value;
    });
    setUrlError(value.length > 8 && !urlRegex.test(value));
  };

  const handleSubmit = () => {
    if (urlError || !formData.url) return;

    if (editingId) {
      updateWebhook.mutate({
        webhookId: editingId,
        url: formData.url,
        actionTypes: formData.actionTypes,
        enabled: formData.enabled,
      });
      setEditingId(null);
    } else {
      createWebhook.mutate({
        url: formData.url,
        actionTypes: formData.actionTypes,
        enabled: formData.enabled,
      });
    }

    setFormData({
      url: "",
      actionTypes: ["startExperience"],
      enabled: true,
    });
  };

  const handleEdit = (webhook: WebhookDto) => {
    setFormData({
      url: webhook.url,
      actionTypes: webhook.actionTypes,
      enabled: webhook.enabled,
    });
    setEditingId(webhook.id);
  };

  const handleCancel = () => {
    setFormData({
      url: "",
      actionTypes: ["startExperience"],
      enabled: true,
    });
    setEditingId(null);
    setUrlError(false);
  };

  const handleDelete = (webhookId: string) => {
    deleteWebhook.mutate(webhookId);
  };

  const handleToggleEnabled = (webhook: WebhookDto) => {
    updateWebhook.mutate({
      webhookId: webhook.id,
      enabled: !webhook.enabled,
    });
  };

  return (
    <Grid item>
      <IgPaper>
        <Grid container direction="column" rowSpacing={3}>
          <Grid item>
            <IgTypography variant={"h3"} sx={{ fontWeight: 600 }}>
              Webhook Settings
            </IgTypography>
          </Grid>

          <Grid item>
            <IgTypography color="text.secondary">
              Configure webhooks to receive notifications when experience
              actions occur. Webhooks will be sent as HTTP POST requests to the
              specified URLs.
              <br />
              For example,{" "}
              <IgLink onClick={() => openNewTab(zapierWebhookExample)}>
                {"notify a Slack channel via Zapier"}
              </IgLink>
              .
            </IgTypography>
          </Grid>
          <Grid container item alignItems={"center"}>
            <Grid item xs={10.5}>
              <IgTextField
                fullWidth
                disabled={isLoading}
                label="Webhook URL"
                value={formData.url}
                onChange={(e) => handleUrlChange(e.target.value)}
                error={urlError}
                helperText={urlError ? "Invalid URL format" : ""}
                placeholder="https://your-domain.com/webhook"
              />
            </Grid>
            <Grid
              item
              sx={{
                marginLeft: "25px",
                marginTop: "20px",
              }}
            >
              <IgSwitch
                disabled={isLoading}
                label={formData.enabled ? "Enabled" : "Disabled"}
                checked={formData.enabled}
                onChange={(e) =>
                  setFormData((draft) => {
                    draft.enabled = e.target.checked;
                  })
                }
              />
            </Grid>
          </Grid>
          <Grid item>
            <Grid item xs={12}>
              <IgAutocomplete
                multiple
                disabled={isLoading}
                options={ExperienceWebhookActionTypeList}
                value={formData.actionTypes}
                onChange={(_, newValue) =>
                  setFormData((draft) => {
                    draft.actionTypes = newValue;
                  })
                }
                clearIcon={<X size={16} />}
                getOptionLabel={(option) => startCase(option)}
                disableCloseOnSelect
                renderInput={(params) => (
                  <IgTextField {...params} label="Action Types" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3} sx={{ marginTop: "20px" }}>
              <Grid container spacing={1}>
                <Grid item>
                  <IgButton
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={urlError || !formData.url}
                    loading={isLoading}
                  >
                    {editingId ? "Update" : "Create"} Webhook
                  </IgButton>
                </Grid>
                {editingId && (
                  <Grid item>
                    <IgButton variant="outlined" onClick={handleCancel}>
                      Cancel
                    </IgButton>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <WebhookTable
              webhooks={webhooks.data || []}
              isLoading={webhooks.isLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleEnabled={handleToggleEnabled}
              editingId={editingId}
              deleteWebhook={deleteWebhook}
              updateWebhook={updateWebhook}
            />
          </Grid>
        </Grid>
      </IgPaper>
    </Grid>
  );
};
